import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { refillService } from '../../services/refillService';
import { rentalService } from '../../services/rentalService';

interface RefillScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ── shared style tokens ──────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  background: '#faf7ee',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid rgba(239,232,213,0.6)',
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  color: '#5b5a52',
  whiteSpace: 'nowrap',
};

const primaryBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 22px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  cursor: 'pointer',
};

const dangerBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(192,80,77,0.3)',
  borderRadius: 4,
  padding: '4px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#c0504d',
  cursor: 'pointer',
};

export const RefillScheduleModal: React.FC<RefillScheduleModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'create'>('upcoming');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [scheduleForm, setScheduleForm] = useState({
    contractId: '',
    machineId: '',
    scheduleType: 'ON_DEMAND' as 'AUTO' | 'ON_DEMAND',
    nextRefillDate: new Date().toISOString().split('T')[0],
    intervalDays: 30
  });

  const { data: upcomingRefills, isLoading: upcomingLoading } = useQuery({
    queryKey: ['due-refills', 'all'],
    queryFn: () => refillService.getDueRefills(),
    enabled: isOpen && activeTab === 'upcoming'
  });

  const { data: pastVisits, isLoading: pastLoading } = useQuery({
    queryKey: ['past-refills', dateRange],
    queryFn: () => refillService.getRefillVisits({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    }),
    enabled: isOpen && activeTab === 'past'
  });

  const { data: contractsData } = useQuery({
    queryKey: ['rental-contracts', { status: 'ACTIVE' }],
    queryFn: () => rentalService.getRentalContracts({ status: 'ACTIVE' as any, limit: 100 }),
    enabled: isOpen && activeTab === 'create'
  });

  const { data: selectedContract } = useQuery({
    queryKey: ['rental-contract', scheduleForm.contractId],
    queryFn: () => rentalService.getRentalContract(scheduleForm.contractId),
    enabled: !!scheduleForm.contractId && activeTab === 'create'
  });

  const createScheduleMutation = useMutation({
    mutationFn: refillService.createRefillSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['due-refills'] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard'] });
      setScheduleForm({
        contractId: '',
        machineId: '',
        scheduleType: 'ON_DEMAND',
        nextRefillDate: new Date().toISOString().split('T')[0],
        intervalDays: 30
      });
      setActiveTab('upcoming');
      alert('Refill schedule created successfully!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to create schedule');
    }
  });

  const deactivateScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      refillService.updateRefillSchedule(scheduleId, { isActive: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['due-refills'] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard'] });
      alert('Schedule deactivated successfully!');
    }
  });

  const contracts = contractsData?.data || [];
  const upcoming = upcomingRefills?.data || [];
  const past = pastVisits?.data || [];

  const machinesWithSchedules = new Set(
    upcoming
      .filter((schedule: any) => schedule.isActive && schedule.contractId === scheduleForm.contractId)
      .map((schedule: any) => schedule.machineId)
  );

  const rentedMachines = selectedContract?.machines?.filter(
    m => m.status === 'RENTED' && !machinesWithSchedules.has(m.id)
  ) || [];

  const handleCreateSchedule = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheduleForm.contractId || !scheduleForm.machineId || !scheduleForm.nextRefillDate) {
      alert('Please fill in all required fields');
      return;
    }
    createScheduleMutation.mutate(scheduleForm);
  };

  const tabActive: React.CSSProperties = {
    background: 'linear-gradient(135deg, #d97706, #b45309)',
    color: '#1c2033',
    border: 'none',
    borderRadius: 4,
    padding: '8px 18px',
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 600,
  };

  const tabInactive: React.CSSProperties = {
    background: 'transparent',
    color: '#6b6a5c',
    border: '1px solid rgba(92,78,51,0.25)',
    borderRadius: 4,
    padding: '8px 18px',
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 900, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '24px 32px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              Refill Schedule Management
            </h2>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setActiveTab('upcoming')} style={activeTab === 'upcoming' ? tabActive : tabInactive}>
              Upcoming ({upcoming.length})
            </button>
            <button onClick={() => setActiveTab('past')} style={activeTab === 'past' ? tabActive : tabInactive}>
              Past Visits
            </button>
            <button onClick={() => setActiveTab('create')} style={activeTab === 'create' ? tabActive : tabInactive}>
              Create Schedule
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>

          {/* Upcoming Tab */}
          {activeTab === 'upcoming' && (
            <div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: '0 0 20px' }}>
                Upcoming Refills (Next 7 Days)
              </h3>
              {upcomingLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  Loading upcoming schedules...
                </div>
              ) : upcoming.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  No upcoming refills scheduled for the next 7 days
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Contract</th>
                        <th style={thStyle}>Customer</th>
                        <th style={thStyle}>Machine</th>
                        <th style={thStyle}>Next Refill Date</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Interval</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.map((schedule: any) => (
                        <tr
                          key={schedule.id}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(241,245,249,1)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={tdStyle}>{schedule.contract?.contractNumber}</td>
                          <td style={tdStyle}>
                            <div style={{ color: '#1c2033' }}>{schedule.contract?.customer?.name}</div>
                            {schedule.contract?.customer?.phone && (
                              <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{schedule.contract.customer.phone}</div>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ color: '#1c2033' }}>{schedule.machine?.serialNumber?.serialNumber}</div>
                            <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{schedule.machine?.serialNumber?.product?.name}</div>
                          </td>
                          <td style={tdStyle}>{new Date(schedule.nextRefillDate).toLocaleDateString()}</td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: 12,
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 9,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              display: 'inline-block',
                              background: schedule.scheduleType === 'AUTO' ? 'rgba(100,200,100,0.12)' : 'rgba(92,78,51,0.22)',
                              color: schedule.scheduleType === 'AUTO' ? '#6fc96f' : '#d97706',
                              border: schedule.scheduleType === 'AUTO' ? '1px solid rgba(100,200,100,0.25)' : '1px solid rgba(92,78,51,0.35)',
                            }}>
                              {schedule.scheduleType}
                            </span>
                          </td>
                          <td style={tdStyle}>{schedule.intervalDays} days</td>
                          <td style={tdStyle}>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to deactivate this schedule?')) {
                                  deactivateScheduleMutation.mutate(schedule.id);
                                }
                              }}
                              style={dangerBtn}
                            >
                              Deactivate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Past Tab */}
          {activeTab === 'past' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                  Past Refill Visits
                </h3>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{ ...inputStyle, width: 'auto' }}
                  />
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{ ...inputStyle, width: 'auto' }}
                  />
                </div>
              </div>

              {pastLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  Loading past visits...
                </div>
              ) : past.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                  No refill visits found for the selected date range
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {past.map((visit: any) => (
                    <div key={visit.id} style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, color: '#1c2033' }}>
                            {visit.contract?.contractNumber} - {visit.contract?.customer?.name}
                          </div>
                          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginTop: 4 }}>
                            Visit #{visit.visitNumber} on {new Date(visit.visitDate).toLocaleDateString()}
                          </div>
                          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginTop: 2 }}>
                            Employee: {visit.employee?.firstName} {visit.employee?.lastName}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 600, color: '#d97706' }}>
                            {visit.totalOilUsedML.toFixed(0)} ML
                          </div>
                          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                            {visit.details?.length || 0} machine(s)
                          </div>
                        </div>
                      </div>

                      {visit.details && visit.details.length > 0 && (
                        <div style={{ borderTop: '1px solid rgba(92,78,51,0.15)', paddingTop: 12, marginTop: 4 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ ...thStyle, background: 'transparent', paddingLeft: 0, paddingTop: 0 }}>Machine</th>
                                <th style={{ ...thStyle, background: 'transparent', paddingTop: 0 }}>Oil Scent</th>
                                <th style={{ ...thStyle, background: 'transparent', textAlign: 'right', paddingRight: 0, paddingTop: 0 }}>Amount (ML)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visit.details.map((detail: any, index: number) => (
                                <tr key={index}>
                                  <td style={{ ...tdStyle, paddingLeft: 0, paddingBottom: 4, paddingTop: 4 }}>{detail.machine?.serialNumber?.serialNumber}</td>
                                  <td style={{ ...tdStyle, paddingBottom: 4, paddingTop: 4 }}>{detail.oilScentName}</td>
                                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 0, paddingBottom: 4, paddingTop: 4 }}>{detail.amountML.toFixed(0)} ML</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {visit.notes && (
                        <div style={{ marginTop: 8, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', fontStyle: 'italic' }}>
                          Note: {visit.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: '0 0 24px' }}>
                Create New Refill Schedule
              </h3>

              <form onSubmit={handleCreateSchedule} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Contract */}
                  <div>
                    <label style={labelStyle}>Rental Contract <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={scheduleForm.contractId}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, contractId: e.target.value, machineId: '' }))}
                      style={inputStyle}
                      required
                    >
                      <option value="">Select Contract</option>
                      {contracts.map((contract: any) => (
                        <option key={contract.id} value={contract.id}>
                          {contract.contractNumber} - {contract.customer?.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Machine */}
                  <div>
                    <label style={labelStyle}>Machine <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={scheduleForm.machineId}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, machineId: e.target.value }))}
                      style={{ ...inputStyle, opacity: !scheduleForm.contractId ? 0.5 : 1 }}
                      disabled={!scheduleForm.contractId}
                      required
                    >
                      <option value="">
                        {!scheduleForm.contractId
                          ? 'Select a contract first'
                          : rentedMachines.length === 0
                          ? 'No machines available (all have schedules)'
                          : 'Select Machine'}
                      </option>
                      {rentedMachines.map((machine: any) => (
                        <option key={machine.id} value={machine.id}>
                          {machine.serialNumber?.serialNumber} - {machine.serialNumber?.product?.name}
                        </option>
                      ))}
                    </select>
                    {scheduleForm.contractId && rentedMachines.length === 0 && (
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', marginTop: 4 }}>
                        All machines in this contract already have active refill schedules.
                      </p>
                    )}
                  </div>

                  {/* Schedule Type */}
                  <div>
                    <label style={labelStyle}>Schedule Type <span style={{ color: '#c0504d' }}>*</span></label>
                    <select
                      value={scheduleForm.scheduleType}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, scheduleType: e.target.value as 'AUTO' | 'ON_DEMAND' }))}
                      style={inputStyle}
                    >
                      <option value="ON_DEMAND">On Demand</option>
                      <option value="AUTO">Automatic</option>
                    </select>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                      {scheduleForm.scheduleType === 'AUTO'
                        ? 'Schedule will auto-renew after each refill'
                        : 'Manual scheduling for customer-requested visits'}
                    </p>
                  </div>

                  {/* Next Refill Date */}
                  <div>
                    <label style={labelStyle}>Next Refill Date <span style={{ color: '#c0504d' }}>*</span></label>
                    <input
                      type="date"
                      value={scheduleForm.nextRefillDate}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, nextRefillDate: e.target.value }))}
                      style={inputStyle}
                      required
                    />
                  </div>

                  {/* Interval Days */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Interval (Days) <span style={{ color: '#c0504d' }}>*</span></label>
                    <input
                      type="number"
                      min="1"
                      value={scheduleForm.intervalDays}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, intervalDays: parseInt(e.target.value) }))}
                      style={inputStyle}
                      required
                    />
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                      Number of days between refills (typically 30 days for monthly service)
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setScheduleForm({
                      contractId: '',
                      machineId: '',
                      scheduleType: 'ON_DEMAND',
                      nextRefillDate: new Date().toISOString().split('T')[0],
                      intervalDays: 30
                    })}
                    style={ghostBtn}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={createScheduleMutation.isPending}
                    style={{ ...primaryBtn, opacity: createScheduleMutation.isPending ? 0.6 : 1, cursor: createScheduleMutation.isPending ? 'not-allowed' : 'pointer' }}
                  >
                    {createScheduleMutation.isPending ? 'Creating...' : 'Create Schedule'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 32px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn}>Close</button>
        </div>
      </div>
    </div>
  );
};
